import { useEffect, useState } from "react";
import { Box, Button, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useColorScheme } from "@mui/material/styles";
import { AccountMenu, AppBar, useActiveShow, userFirstName } from "common-fe";
import { useAuth } from "react-oidc-context";
import { Logo, colors } from "brand";
import { useTranslation } from "i18n-fe";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import LogoutIcon from "@mui/icons-material/Logout";
import InboxIcon from "@mui/icons-material/Inbox";
import { useScoreContext } from "../context/ScoreContext";
import { fullSignOut } from "../auth/signOut";
import { roleLabel } from "../util/roleLabels";
import InstallButton from "./InstallButton";
import SessionExpiredChip from "./SessionExpiredChip";
import StoragePersistenceChip from "./StoragePersistenceChip";
import VenueClock from "./VenueClock";

// Decode a JWT payload without verifying it (verification is the API's job).
function decodeJwt(token) {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(payload));
  } catch {
    return {};
  }
}

// #258 — the assignable roles come from the Cognito access token's `roles`
// claim ({ orgId: [role, …] }, stamped by the PreToken trigger). Flatten
// across orgs into a unique list; a user with fewer than two roles sees no
// switcher.
function rolesFromUser(user) {
  const claims = user?.access_token ? decodeJwt(user.access_token) : {};
  const raw = claims.roles ?? claims["custom:roles"];
  let byOrg = {};
  try {
    byOrg = typeof raw === "string" ? JSON.parse(raw) : raw || {};
  } catch {
    byOrg = {};
  }
  const set = new Set();
  Object.values(byOrg).forEach((list) => (list || []).forEach((r) => set.add(r)));
  return [...set];
}

// #258 — top-nav role switcher. A user assigned more than one role can
// switch their active view here without re-authenticating; single-role
// users see nothing. Stock MUI Button + Menu.
function RoleSwitcher() {
  const { user } = useAuth();
  const { activeRole, setActiveRole } = useScoreContext();
  const { t } = useTranslation("common");
  const [anchorEl, setAnchorEl] = useState(null);
  const roles = rolesFromUser(user);
  const current = activeRole && roles.includes(activeRole) ? activeRole : roles[0];

  // Default the active role to the first assigned role.
  useEffect(() => {
    if (roles.length > 0 && (!activeRole || !roles.includes(activeRole))) {
      setActiveRole(roles[0]);
    }
  }, [roles, activeRole, setActiveRole]);

  if (roles.length < 2) return null;

  return (
    <>
      <Button
        data-testid="role-switcher"
        color="inherit"
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        endIcon={<ArrowDropDownIcon />}
        // On the dark AppBar; inherit keeps the label white and legible.
        sx={{ color: "inherit", fontWeight: 600, textTransform: "none" }}
      >
        {roleLabel(t, current)}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {roles.map((role) => (
          <MenuItem
            key={role}
            data-testid={`role-option-${role}`}
            selected={role === current}
            onClick={() => {
              setActiveRole(role);
              setAnchorEl(null);
            }}
          >
            {roleLabel(t, role)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

// #3093 — always-visible entry point to /outbox on every TopBar screen,
// whether or not anything is pending (SyncStatus's chip only exists when
// there is something to show, and #3016's chip-tap is not discoverable on
// its own). A real router link: stays inside the app, no escape hatch.
// The text collapses to icon-only below `sm` to protect the crowded top
// bar; the aria-label keeps the accessible name stable at every width.
function OutboxLink() {
  const { t } = useTranslation("score");
  const label = t("outboxPage.title");
  return (
    <Button
      component={RouterLink}
      to="/outbox"
      data-testid="topbar-outbox-link"
      aria-label={label}
      color="inherit"
      size="small"
      startIcon={<InboxIcon />}
      sx={{
        color: "inherit",
        fontWeight: 600,
        textTransform: "none",
        minWidth: 0,
        "& .MuiButton-startIcon": { mr: { xs: 0, sm: 1 }, ml: { xs: 0, sm: -0.5 } },
      }}
    >
      <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
        {label}
      </Box>
    </Button>
  );
}

// Brand AppBar + wordmark matching manager-fe / scoreadmin-fe.
function BrandWordmark() {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  return (
    <Box
      data-testid="topbar-home"
      onClick={() => navigate("/classes")}
      sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
    >
      <Logo
        style={{
          paddingLeft: 8,
          height: 40,
          width: "auto",
          marginRight: 16,
          flexShrink: 0,
        }}
      />
      <Typography variant="h2" sx={{ fontSize: "1.1rem" }}>
        {t("app.title")}
      </Typography>
    </Box>
  );
}

export default function TopBar() {
  const { user, removeUser } = useAuth();
  const { selectedShow } = useScoreContext();
  const { activeShowId, setActiveShowId } = useActiveShow();

  // #521/#628 — the scribing session (restored from localStorage per #480) is the
  // source of truth for the active show; push it into the shared show context so
  // API calls are scoped to it. There is no app-bar switcher any more: changing
  // the show is PIN-gated (the lock → /setup), so it can't be done in passing.
  useEffect(() => {
    if (selectedShow?.id && selectedShow.id !== activeShowId) {
      setActiveShowId(selectedShow.id);
    }
  }, [selectedShow?.id, activeShowId, setActiveShowId]);
  // #207/#736 — show the signed-in scribe's name beside the profile icon so
  // admins can see who is currently scribing. Header rule (bug #87):
  // FIRST NAME only, falling back to the full email (never a raw/shortened
  // local-part) when no name is on record — routed through the shared
  // common-fe helper so this stays consistent with every other header.
  const scribeName = userFirstName(user?.profile) || null;
  const { mode, systemMode, setMode } = useColorScheme();
  // #263 — base the toggle on the RESOLVED appearance, not the raw mode.
  // Until the user explicitly picks one, `mode` is "system" (neither
  // "light" nor "dark"), so the old `mode === "dark"` check was false
  // even when the screen was actually showing dark — the first click
  // then set "dark" and nothing visibly changed; only the second click
  // worked. Resolving "system" to the OS appearance makes the very
  // first toggle flip it.
  const resolvedMode = mode === "system" ? systemMode : mode;

  const handleThemeToggle = () => {
    setMode(resolvedMode === "dark" ? "light" : "dark");
  };

  const accountMenuItems = [
    // #734 — kiosk lockdown: "Account settings" is no longer a free
    // escape hatch on this menu. It now lives inside the PIN-locked
    // admin screen (InitialSetup), reachable only after the PIN.
    {
      icon: resolvedMode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />,
      text: resolvedMode === "dark" ? "Light mode" : "Dark mode",
      onClick: handleThemeToggle,
    },
    {
      icon: <LogoutIcon />,
      text: "Sign out",
      // #519 — full sign-out: purge the OIDC user + persisted scribe session,
      // then end the pool session. (#627 removed "Change show / position": the
      // show is picked once and kept across logins (#480); changing it is a
      // deliberate, PIN-gated act. Sign out is the clean-slate exit.)
      onClick: () => fullSignOut(removeUser),
    },
  ];

  return (
    <AppBar
      sx={{ backgroundColor: colors.darkGrey, color: "#fff" }}
      brandLogo={<BrandWordmark />}
      rightContent={
        <Stack direction="row" spacing={1} alignItems="center">
          {/* #1889 — venue-local running clock, on every scribe view via
              TopBar. Its own ticking state lives entirely inside VenueClock
              (see that file); a tick re-renders only this leaf, never TopBar
              or the routed page content beside it. */}
          <VenueClock venueTimezone={selectedShow?.venueTimezone} />
          <OutboxLink />
          <InstallButton />
          <RoleSwitcher />
          {scribeName && (
            <Typography
              data-testid="scribe-name"
              variant="body2"
              noWrap
              sx={{
                // On the dark AppBar (#fff text); inherit keeps it white
                // and legible rather than a dim themed color.
                color: "inherit",
                fontWeight: 600,
                maxWidth: { xs: 96, sm: 220 },
              }}
            >
              {scribeName}
            </Typography>
          )}
          {/* #1701 — moved off SessionHeader's secondary nav (bug #136): it
              was easy to miss down there, and it was taking width from the
              row that was wrapping (bug #133). Lives on the top nav line,
              right next to the profile avatar, so it stays equally visible
              and tappable without competing for space with the breadcrumb/
              ride chips below. */}
          <SessionExpiredChip />
          <StoragePersistenceChip />
          <AccountMenu menuItems={accountMenuItems} />
        </Stack>
      }
    />
  );
}
